import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Filter, Check, ArrowUpDown } from "lucide-react";
import { theme } from "@/lib/theme";

interface ChitraNavigationProps {
    isDarkMode: boolean;
    filter: string;
    onFilterChange: (filter: string) => void;
    filterOptions: { value: string; label: string }[];
    sortBy?: 'recent' | 'likes';
    onSortChange?: (sort: 'recent' | 'likes') => void;
}

export const ChitraNavigation: React.FC<ChitraNavigationProps> = ({
    isDarkMode,
    filter,
    onFilterChange,
    filterOptions,
    sortBy = 'recent',
    onSortChange,
}) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Close filter menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
        };

        if (isFilterOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isFilterOpen]);

    return (
        <div className="flex items-center gap-2 select-none">
            <div className="flex items-center bg-black/5 dark:bg-white/5 p-1 rounded-full backdrop-blur-sm">
                <NavLink
                    to="/chitra/home"
                    className={({ isActive }) =>
                        cn(
                            "px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                            isActive
                                ? isDarkMode
                                    ? "bg-white/10 text-white shadow-sm"
                                    : "bg-white text-black shadow-sm"
                                : isDarkMode
                                    ? "text-gray-400 hover:text-gray-200"
                                    : "text-gray-600 hover:text-gray-900"
                        )
                    }
                >
                    Home
                </NavLink>
                <NavLink
                    to="/chitra/library"
                    className={({ isActive }) =>
                        cn(
                            "px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200",
                            isActive
                                ? isDarkMode
                                    ? "bg-white/10 text-white shadow-sm"
                                    : "bg-white text-black shadow-sm"
                                : isDarkMode
                                    ? "text-gray-400 hover:text-gray-200"
                                    : "text-gray-600 hover:text-gray-900"
                        )
                    }
                >
                    Library
                </NavLink>
            </div>

            <div className="relative" ref={filterRef}>
                <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                        "rounded-full h-9 px-3 gap-1.5 transition-colors",
                        isFilterOpen ? "bg-black/10 dark:bg-white/10" : "hover:bg-black/5 dark:hover:bg-white/5"
                    )}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                    <Filter className="h-3.5 w-3.5" />
                </Button>

                {isFilterOpen && (
                    <div
                        className={cn(
                            "absolute right-0 top-full mt-2 w-56 rounded-xl shadow-lg border backdrop-blur-xl z-[9999] overflow-hidden",
                            isDarkMode ? "bg-black/90 border-white/10" : "bg-white/95 border-black/10"
                        )}
                        style={{ animation: "fadeIn 0.2s ease-out" }}
                    >
                        <div className="p-2">
                            {filterOptions.map((option) => (
                                <button
                                    key={`filter-${option.value}`}
                                    onClick={() => {
                                        onFilterChange(option.value);
                                    }}
                                    className={cn(
                                        "w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors text-left mb-1",
                                        filter === option.value
                                            ? isDarkMode
                                                ? "bg-white/10 text-white"
                                                : "bg-black/5 text-black"
                                            : isDarkMode
                                                ? "text-gray-400 hover:text-white hover:bg-white/5"
                                                : "text-gray-600 hover:text-black hover:bg-black/5"
                                    )}
                                >
                                    {option.label}
                                    {filter === option.value && <Check className="h-3.5 w-3.5" />}
                                </button>
                            ))}
                            
                            <div className="border-t border-black/10 dark:border-white/10 my-2"></div>
                            
                            <div className="px-2 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Sort By
                            </div>
                            <button
                                onClick={() => onSortChange?.('recent')}
                                className={cn(
                                    "w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors text-left mb-1",
                                    sortBy === 'recent'
                                        ? isDarkMode
                                            ? "bg-white/10 text-white"
                                            : "bg-black/5 text-black"
                                        : isDarkMode
                                            ? "text-gray-400 hover:text-white hover:bg-white/5"
                                            : "text-gray-600 hover:text-black hover:bg-black/5"
                                )}
                            >
                                Most Recent
                                {sortBy === 'recent' && <Check className="h-3.5 w-3.5" />}
                            </button>
                            <button
                                onClick={() => onSortChange?.('likes')}
                                className={cn(
                                    "w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors text-left",
                                    sortBy === 'likes'
                                        ? isDarkMode
                                            ? "bg-white/10 text-white"
                                            : "bg-black/5 text-black"
                                        : isDarkMode
                                            ? "text-gray-400 hover:text-white hover:bg-white/5"
                                            : "text-gray-600 hover:text-black hover:bg-black/5"
                                )}
                            >
                                Most Liked
                                {sortBy === 'likes' && <Check className="h-3.5 w-3.5" />}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
